import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { api, type Me } from "../api";
import { BackIcon } from "../components/icons";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import type { UiLanguage } from "../i18n";

const MAX_AGE = 120;
const MAX_AUTHOR_NAME_LEN = 120;

/**
 * Editable view of the same fields collected during onboarding. Reachable
 * from the avatar in the top-left of Home; saving writes back through
 * PATCH /api/me and notifies the App so the cached `me` follows.
 *
 * Stored value is the birth year, but the field is presented (and edited)
 * as the artist's current age — easier to enter and the year falls out of
 * `currentYear - age`.
 */
export default function Account({ me, onSaved }: { me: Me; onSaved: (me: Me) => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const thisYear = new Date().getFullYear();
  const initialAge = me.authorBirthYear ? String(thisYear - me.authorBirthYear) : "";
  const [name, setName] = useState(me.authorName ?? "");
  const [age, setAge] = useState<string>(initialAge);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [gitSha, setGitSha] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api.version().then((v) => {
      if (!cancelled) setGitSha(v.gitSha);
    }).catch(() => { /* version is a passive readout — stay silent on failure */ });
    return () => { cancelled = true; };
  }, []);

  // Auto-clear the "Saved" banner so it doesn't linger if the user pauses on
  // the screen.
  useEffect(() => {
    if (!savedAt) return;
    const t = window.setTimeout(() => setSavedAt(null), 2500);
    return () => window.clearTimeout(t);
  }, [savedAt]);

  const trimmed = name.trim();
  const ageNum = Number(age);
  const ageOk = age !== "" && Number.isInteger(ageNum) && ageNum >= 0 && ageNum <= MAX_AGE;
  const nextBirthYear = ageOk ? thisYear - ageNum : null;
  const dirty =
    trimmed !== (me.authorName ?? "") ||
    nextBirthYear !== (me.authorBirthYear ?? null);
  const canSave = trimmed.length > 0 && ageOk && dirty && !busy;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSave || nextBirthYear == null) return;
    setBusy(true);
    setErr(null);
    try {
      const updated = await api.updateMe({
        authorName: trimmed,
        authorBirthYear: nextBirthYear,
      });
      onSaved(updated);
      setSavedAt(Date.now());
    } catch (e2) {
      setErr(String(e2));
    } finally {
      setBusy(false);
    }
  };

  // The language switcher applies immediately (and caches to localStorage via
  // the detector); we also persist it to the profile so it follows the user
  // across devices. Best-effort — a failed write just leaves the local choice.
  const persistLanguage = (lng: UiLanguage) => {
    void api
      .updateMe({ uiLanguage: lng })
      .then(onSaved)
      .catch(() => { /* keep the local choice; server sync can retry later */ });
  };

  return (
    <div className="tp-app">
      <div className="tp-topbar">
        <button className="tp-topbtn" onClick={() => navigate("/app")} aria-label={t("common.back")}>
          <BackIcon />
        </button>
        <div className="tp-eyebrow">{t("account.title")}</div>
        <div style={{ width: 38 }} />
      </div>

      <form className="tp-screen" onSubmit={submit} style={{ paddingTop: 24 }}>
        <div className="tp-display" style={{ marginBottom: 8 }}>{t("account.title")}</div>
        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 24, fontSize: 16 }}>
          <Trans
            i18nKey="account.signedInAs"
            values={{ email: me.email }}
            components={{ e: <strong style={{ color: "var(--tp-ink)" }} /> }}
          />
        </div>
        <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 24, fontSize: 16 }}>
          {t("account.credits")}{" "}
          <strong style={{ color: "var(--tp-ink)" }}>
            {me.credits?.unlimited ? t("account.unlimited") : me.credits?.balance ?? 0}
          </strong>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>
            {t("common.artistName")}
          </label>
          <input
            className="tp-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t("onboarding.namePlaceholder")}
            maxLength={MAX_AUTHOR_NAME_LEN}
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>
            {t("common.age")}
          </label>
          <input
            className="tp-input"
            type="number"
            inputMode="numeric"
            value={age}
            onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
            placeholder={t("onboarding.agePlaceholder")}
            min={0}
            max={MAX_AGE}
          />
        </div>

        <div style={{ marginBottom: 22 }}>
          <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>
            {t("language.label")}
          </label>
          <LanguageSwitcher persist={persistLanguage} />
        </div>

        {err && <div className="tp-error">{err}</div>}
        {savedAt && (
          <div className="tp-body" style={{ color: "var(--tp-ink-soft)", marginBottom: 12, fontSize: 14 }}>
            {t("account.saved")}
          </div>
        )}

        <button type="submit" className="tp-btn tp-btn-primary tp-btn-block" disabled={!canSave}>
          {busy ? t("common.saving") : t("account.saveChanges")}
        </button>

        <div style={{ marginTop: 28 }}>
          <button
            type="button"
            className="tp-btn tp-btn-block"
            onClick={async () => {
              await api.logout();
              window.location.href = "/";
            }}
          >
            {t("common.signOut")}
          </button>
        </div>

        <div className="tp-body" style={{ textAlign: "center", fontSize: 13, color: "var(--tp-ink-soft)", marginTop: 18 }}>
          <a
            href="/legal"
            onClick={(e) => { e.preventDefault(); navigate("/legal"); }}
            style={{ color: "var(--tp-ink-soft)", textDecoration: "underline" }}
          >
            {t("account.termsPrivacy")}
          </a>
        </div>

        {me.isAdmin && (
          <div className="tp-body" style={{ textAlign: "center", fontSize: 13, color: "var(--tp-ink-soft)", marginTop: 8 }}>
            <a
              href="/app/admin"
              onClick={(e) => { e.preventDefault(); navigate("/app/admin"); }}
              style={{ color: "var(--tp-ink-soft)", textDecoration: "underline" }}
            >
              {t("admin.openAdmin")}
            </a>
          </div>
        )}

        {gitSha && (
          <div
            className="tp-body"
            style={{ textAlign: "center", fontSize: 12, color: "var(--tp-ink-soft)", marginTop: 8 }}
          >
            {t("account.build", { sha: gitSha.slice(0, 7) })}
          </div>
        )}
      </form>
    </div>
  );
}
